import React, { useState } from "react";
import PropTypes from "prop-types";
import UserCard from "../../ui/userCard";
import SkillsCard from "../../ui/skillsCard";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUserById, updateUser } from "../../store/users";
import { deleteIdentify, getIdentifyById } from "../../store/identify";
import Results from "../../components/Results";
import { deleteGroup, getGroupById } from "../../store/groups";
import { createDeletedElem } from "../../store/delete";
import ConfirmationForm from "../../components/common/form/ConfirmationForm";
import SelectTown from "../../components/SelectTown";
import { getCurrentUserInvitation } from "../../store/invitations";
import { getTownById } from "../../store/towns";
import { useI18n } from "../../i18n/LanguageContext";

const UserPage = ({ userId }) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const dispatch = useDispatch();
  const { country } = useParams();
  const user = useSelector(getUserById(userId));
  const group = useSelector(getGroupById(user && user?.teamId));
  const invitation = useSelector(getCurrentUserInvitation());

  const town = useSelector(getTownById(user.town));

  const identify = useSelector(getIdentifyById(user?.teamId));

  const handleClick = () => {
    navigate(`/${country}`);
  };

  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleConfirm = () => {
    const deletedMembers = group.members.filter((elem) => {
      return elem.member !== user._id;
    });

    deletedMembers.forEach((elem) => {
      dispatch(
        createDeletedElem({
          userId: elem.member,
          removeInvitation: false,
        })
      );
    });
    !identify && dispatch(deleteIdentify({ groupId: user?.teamId }));
    dispatch(deleteGroup({ groupId: user?.teamId }));

    dispatch(updateUser({ ...user, teamId: "" }));
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleDelete = async () => {
    if (group?.status === "build") {
      await group.members.forEach((m) => {
        if (m.accept === false && m.member.toString() !== userId) {
          dispatch(
            createDeletedElem({
              userId: m.member,
              removeInvitation: true,
            })
          );
        } else if (m.accept === true && m.member.toString() !== userId) {
          dispatch(
            createDeletedElem({
              userId: m.member,
              removeInvitation: false,
            })
          );
        }
      });
      // удаляем идентификатор при наличии
      identify !== null && dispatch(deleteIdentify({ groupId: user.teamId }));
      // удаляем группу
      dispatch(deleteGroup({ groupId: group._id }));
      dispatch(updateUser({ ...user, teamId: "" }));
    } else {
      console.log("error in delete group");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Основная карточка пользователя */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 mb-8">
          <div className="grid gap-6 lg:gap-8 grid-cols-1 lg:grid-cols-3 p-6 lg:p-8">
            {/* Карточка пользователя */}
            <div className="lg:col-span-1">
              <UserCard user={user} />
            </div>

            {/* Информация о команде и навыки */}
            <div className="lg:col-span-2 space-y-6">
              {/* Название команды */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                <span className="text-indigo-700 font-semibold whitespace-nowrap">
                  {t("user.teamName")}
                </span>
                <div className="text-indigo-900 font-bold text-lg">
                  {user?.teamId
                    ? (group && group.teamname) || t("user.loading")
                    : t("user.noTeam")}
                </div>
              </div>

              {/* Навыки */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <SkillsCard data={user.skills} />
              </div>
            </div>
          </div>
        </div>

        {/* Город */}
        {group === null || (group === undefined && invitation === undefined) ? (
          <SelectTown />
        ) : (
          <div className="flex items-center justify-center gap-3 py-4 mb-8 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <h2 className="text-gray-600 font-medium">{t("user.city")}</h2>
            <p className="text-gray-800 font-semibold">{town?.name}</p>
          </div>
        )}

        {/* Действия с группой */}
        <div className="space-y-6 mb-8">
          {/* Удаление группы в статусе play */}
          {group?.status === "play" && !group?.missionId && (
            <div className="bg-white rounded-2xl shadow-lg border border-red-100 p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <p className="text-gray-700 font-medium">
                    {t("user.manage")}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <ConfirmationForm
                    handleCancel={handleCancel}
                    open={open}
                    handleConfirm={handleConfirm}
                  />
                  <button
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                    onClick={handleOpen}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    {t("user.delete")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Управление группой в статусе build */}
          {group?.status === "build" && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
                  <div className="text-yellow-800">
                    <p className="font-semibold">{t("user.incomplete")}</p>
                    <p className="text-sm">{t("user.waitOrDelete")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <ConfirmationForm
                    handleCancel={handleCancel}
                    open={open}
                    handleConfirm={handleDelete}
                  />
                  <button
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                    onClick={handleOpen}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    {t("user.disband")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Результаты */}
        {user?.teamId && (
          <div className="mb-8">
            <Results />
          </div>
        )}

        {/* Кнопка назад */}
        <div className="flex justify-center">
          <button
            onClick={handleClick}
            className="group px-8 py-4 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-3"
          >
            <svg
              className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            {t("common.back")}
          </button>
        </div>
      </div>
    </div>
  );
  // return (
  //   <div className="container mb-10 text-lg">
  //     <div className="flex-row sm:mx-10 mt-24 border-solid border-2 border-indigo-600">
  //       <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
  //         <UserCard user={user} />
  //         <div className="col-span-2 m-5">
  //           <div className="flex flex-row">
  //             <p className="self-center mr-10">Team name:</p>
  //             <div className="text-secondary my-2">
  //               {user?.teamId ? group && group.teamname : "не состоит"}
  //             </div>
  //           </div>
  //           <SkillsCard data={user.skills} />
  //         </div>
  //       </div>
  //     </div>
  //     {group === null || (group === undefined && invitation === undefined) ? (
  //       <SelectTown />
  //     ) : (
  //       <div className="flex flex-row justify-center my-10 gap-4 mx-3 border-b text-gray-400">
  //         <h2>город</h2>
  //         <p>{town?.name}</p>
  //       </div>
  //     )}

  //     {group?.status === "play" && !group?.missionId && (
  //       <div className="flex flex-row justify-end mx-10 border-solid my-10">
  //         <div className="flex px-4 py-2 text">Удалить группу</div>
  //         <ConfirmationForm
  //           handleCancel={handleCancel}
  //           open={open}
  //           handleConfirm={handleConfirm}
  //         />
  //         <button
  //           className="px-6 py-2 rounded bg-red-500 mr-5 hover:bg-red-300"
  //           onClick={handleOpen}
  //         >
  //           delete
  //         </button>
  //       </div>
  //     )}

  //     {group?.status === "build" ? (
  //       <div className="flex flex-col md:flex-row md:justify-end mx-10 border-solid my-10">
  //         <div className="flex px-4 py-2 text">
  //           Группа не полная можете удалить или дождаться подтверждения
  //         </div>
  //         <ConfirmationForm
  //           handleCancel={handleCancel}
  //           open={open}
  //           handleConfirm={handleDelete}
  //         />
  //         <button
  //           className="px-6 py-2 rounded bg-red-500 mr-5 hover:bg-red-300"
  //           onClick={handleOpen}
  //         >
  //           расформировать
  //         </button>
  //       </div>
  //     ) : (
  //       <div></div>
  //     )}

  //     {user?.teamId && <Results />}
  //     <div className="flex justify-center  my-20">
  //       <Button name="back" className=" w-40" handleClick={handleClick} />
  //     </div>
  //   </div>
  // );
};

UserPage.propTypes = {
  userId: PropTypes.string,
};

export default UserPage;
